/**
 * Classification on a project's Rules tab: how sensitive its data is, as one choice with its sentence under it,
 * and, for each repository, whether it keeps the project's or is stricter. Owners set them; everyone reads them.
 * The providers that meet the classification are listed under it (the assurance register).
 */

import { CLASSIFICATION_DESCRIPTIONS, CLASSIFICATION_ORDER, type Classification, type ProjectRoot } from '@aioe/shared';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { providerWord, tierWord } from './assurance.tsx';
import { ChoiceSelect, ErrorNote, ListBlock, ListGroup, ListRow } from './ui.tsx';
import { useLoad } from '../hooks.ts';
import { LEVEL_TEXT } from './memoryAccessModel.ts';

/** The levels in plain words (Public, Internal, Confidential, Restricted); the sentence under each says what it covers. */
const CLASSIFICATION_WORDS = LEVEL_TEXT;

const INHERIT = 'inherit';

/** A repository's classification when it differs from the project's, as words; undefined when it inherits. */
export const ownClassification = (root: { classification?: Classification }, project?: Classification): string | undefined => (root.classification && root.classification !== project ? CLASSIFICATION_WORDS[root.classification] : undefined);

export function ClassificationPanel({ projectId, classification, roots, owner, onChange }: { projectId: string; classification?: Classification; roots: Array<Pick<ProjectRoot, 'rootId' | 'name' | 'classification'>>; owner: boolean; onChange: () => void }) {
  const [error, setError] = useState<string>();
  const assurance = useLoad(() => routes.projectAssurance(projectId).catch(() => undefined), [projectId, classification]);
  const run = async (fn: () => Promise<unknown>) => {
    try {
      setError(undefined);
      await fn();
      onChange();
    } catch (e) {
      setError(problemText(e));
    }
  };
  const options = Object.fromEntries(CLASSIFICATION_ORDER.map((c) => [c, CLASSIFICATION_WORDS[c]])) as Record<Classification, string>;
  const meeting = (assurance.data?.effective.providers ?? []).filter((p) => p.allowed);
  const names = [...new Set(meeting.map((p) => `${providerWord(p.provider)} (${tierWord(p.tier)})`))];
  // A repository may be as strict as the project or stricter: the project's own word is the floor.
  const floor = classification ? CLASSIFICATION_ORDER.indexOf(classification) : 0;
  return (
    <ListGroup title="Classification" description="How sensitive this project's data is. Members read its memory up to it, and it decides which AI providers its work may use; each repository follows the project unless an owner makes it stricter.">
      <ListRow
        title={owner ? 'The project' : classification ? CLASSIFICATION_WORDS[classification] : 'Not classified yet'}
        description={classification ? CLASSIFICATION_DESCRIPTIONS[classification] : 'Until an owner chooses, no provider is held back and its memory counts as Internal.'}
        {...(owner ? { control: <ChoiceSelect label="Classification" value={classification} options={options} placeholder="Not classified yet" onChange={(v) => void run(() => routes.setProjectClassification(projectId, v))} /> } : {})}
      />
      {classification && names.length > 0 ? <ListRow title="Providers that meet it" description={`${names.join(', ')}.`} /> : null}
      {owner
        ? roots.map((r) => {
            // A repository set to the project's own level, or below it, works at the project's: it shows as "Same as the project".
            const choices: Record<string, string> = { [INHERIT]: classification ? `Same as the project (${CLASSIFICATION_WORDS[classification]})` : 'Same as the project' };
            CLASSIFICATION_ORDER.forEach((c, i) => {
              if (i > floor || (i === floor && !classification)) choices[c] = CLASSIFICATION_WORDS[c];
            });
            return (
              <ListRow
                key={r.rootId}
                title={r.name ?? r.rootId}
                description="Repository"
                control={<ChoiceSelect label={`Classification of ${r.name ?? r.rootId}`} value={r.classification && choices[r.classification] ? r.classification : INHERIT} options={choices} onChange={(v) => void run(() => routes.setRootClassification(projectId, r.rootId, v === INHERIT ? null : (v as Classification)))} />}
              />
            );
          })
        : null}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
